import { getSampleEntries, isFieldRequired } from '../../audit/answers';
import { isFieldScored, type AnswerMap, type Question, type YesNoNa } from '../../audit/template';
import { YesNoNaInput } from './QuestionInput';

/**
 * Renders a sample_group as a dynamic list of entries: "verify up to N,
 * minimum M". The auditor adds only as many entries as items exist —
 * no Jotform-style padding of unused slots. Fields with requiredIf (e.g.
 * discrepancies) flip between optional and required per entry.
 */
export function SampleGroupInput({
  question,
  answers,
  missing,
  entryShortfall,
  onFieldChange,
  onAddEntry,
  onRemoveEntry,
  entryActions,
  entryExtras,
}: {
  question: Question;
  answers: AnswerMap;
  /** Keys `${sampleIndex}:${fieldId}` that failed submit validation. */
  missing: ReadonlySet<string>;
  /** Entries still owed toward minCount (0 = satisfied). */
  entryShortfall: number;
  onFieldChange: (
    sampleIndex: number,
    fieldId: string,
    value: string | undefined,
  ) => void;
  onAddEntry: () => void;
  onRemoveEntry: (sampleIndex: number) => void;
  /** Badge buttons rendered in each entry's header (note/task/upload). */
  entryActions?: ((sampleIndex: number) => import('react').ReactNode) | undefined;
  /** Content rendered at the end of each entry (notes, chips, editors). */
  entryExtras?: ((sampleIndex: number) => import('react').ReactNode) | undefined;
}) {
  const max = question.sampleCount ?? 0;
  const min = question.minCount ?? 0;
  const entries = getSampleEntries(answers, question.id);

  return (
    <div className="sg">
      <div className="sg-meta muted">
        {entries.length} of up to {max}
        {min > 0 && ` (minimum ${min})`}
        {entryShortfall > 0 && (
          <span className="error"> — add {entryShortfall} more</span>
        )}
      </div>
      {entries.map((entry, i) => (
        <fieldset className="sg-entry" key={i}>
          <legend>
            Entry {i + 1}
            <button
              type="button"
              className="chip-remove"
              aria-label={`Remove entry ${i + 1}`}
              onClick={() => onRemoveEntry(i)}
            >
              ×
            </button>
          </legend>
          {entryActions && (
            <div className="sg-entry-actions">{entryActions(i)}</div>
          )}
          {(question.fields ?? []).map((field) => {
            const isMissing = missing.has(`${i}:${field.id}`);
            const required = isFieldRequired(field, entry);
            const rawValue = entry[field.id];
            // The field label names its input (screen readers, getByLabel).
            const fieldId = `sg-${question.id}-${i}-${field.id}`;
            return (
              <div
                key={field.id}
                className={isMissing ? 'sg-field missing' : 'sg-field'}
              >
                <label className="sg-label" id={`${fieldId}-label`}>
                  {field.label}
                  {field.requiredIf && (
                    <span className={required ? 'error' : 'muted'}>
                      {required ? ' (required)' : ' (optional)'}
                    </span>
                  )}
                </label>
                {field.type === 'yes_no' || field.type === 'yes_no_na' ? (
                  <YesNoNaInput
                    value={rawValue as YesNoNa | undefined}
                    allowNa={field.type === 'yes_no_na'}
                    neutral={!isFieldScored(field)}
                    onChange={(v) => onFieldChange(i, field.id, v)}
                    labelledBy={`${fieldId}-label`}
                  />
                ) : field.type === 'long_text' ? (
                  <textarea
                    id={fieldId}
                    aria-labelledby={`${fieldId}-label`}
                    className="q-input"
                    rows={2}
                    value={rawValue ?? ''}
                    onChange={(e) =>
                      onFieldChange(i, field.id, e.target.value || undefined)
                    }
                  />
                ) : (
                  <input
                    id={fieldId}
                    aria-labelledby={`${fieldId}-label`}
                    className="q-input"
                    value={rawValue ?? ''}
                    onChange={(e) =>
                      onFieldChange(i, field.id, e.target.value || undefined)
                    }
                  />
                )}
              </div>
            );
          })}
          {entryExtras?.(i)}
        </fieldset>
      ))}
      {entries.length < max && (
        <button type="button" className="btn sg-add" onClick={onAddEntry}>
          + Add entry {entries.length + 1}
        </button>
      )}
    </div>
  );
}
